/**
 * Life4Earth V2 — Custom Theme API
 *
 * CRUD for custom themes and per-theme overrides.
 * Replaces localStorage-only storage so themes propagate
 * across all browsers and to all users.
 */
import { apiFetch } from './client';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

export interface CustomThemeDto {
  name: string;
  baseTheme: 'dark' | 'light';
  tokenOverrides: Record<string, string>;
  isVisible: boolean;
  displayName?: string | null;
  icon?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomThemeRequest {
  name: string;
  baseTheme?: 'dark' | 'light';
  tokenOverrides?: Record<string, string>;
  isVisible?: boolean;
  displayName?: string;
  icon?: string;
}

export interface UpdateCustomThemeRequest {
  newName?: string;
  baseTheme?: 'dark' | 'light';
  tokenOverrides?: Record<string, string>;
  isVisible?: boolean;
  displayName?: string;
  icon?: string;
}

export interface ThemeOverrideDto {
  themeName: string;
  highlightColor?: string | null;
  tokenOverrides: Record<string, string>;
}

export interface SaveThemeOverrideRequest {
  highlightColor?: string;
  tokenOverrides?: Record<string, string>;
}

/* ================================================================== */
/*  Custom Theme CRUD                                                  */
/* ================================================================== */

/** Fetch all custom themes from the API. */
export async function fetchCustomThemes(): Promise<CustomThemeDto[]> {
  return apiFetch<CustomThemeDto[]>('/api/CustomTheme');
}

/** Fetch a single custom theme by name. */
export async function fetchCustomTheme(name: string): Promise<CustomThemeDto> {
  return apiFetch<CustomThemeDto>(`/api/CustomTheme/${encodeURIComponent(name)}`);
}

/** Create a new custom theme. */
export async function createCustomTheme(request: CreateCustomThemeRequest): Promise<CustomThemeDto> {
  return apiFetch<CustomThemeDto>('/api/CustomTheme', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/** Update an existing custom theme. */
export async function updateCustomTheme(name: string, request: UpdateCustomThemeRequest): Promise<CustomThemeDto> {
  return apiFetch<CustomThemeDto>(`/api/CustomTheme/${encodeURIComponent(name)}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

/** Delete a custom theme. */
export async function deleteCustomThemeApi(name: string): Promise<void> {
  await apiFetch<void>(`/api/CustomTheme/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  });
}

/* ================================================================== */
/*  Theme Overrides (built-in + custom)                                */
/* ================================================================== */

/** Fetch all theme overrides. */
export async function fetchThemeOverrides(): Promise<ThemeOverrideDto[]> {
  return apiFetch<ThemeOverrideDto[]>('/api/CustomTheme/overrides');
}

/** Save overrides for a theme (upsert). */
export async function saveThemeOverrideApi(
  themeName: string,
  request: SaveThemeOverrideRequest
): Promise<ThemeOverrideDto> {
  return apiFetch<ThemeOverrideDto>(
    `/api/CustomTheme/overrides/${encodeURIComponent(themeName)}`,
    { method: 'PUT', body: JSON.stringify(request) }
  );
}
